import { DroneLogo } from './DroneLogo';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { displayImageUrl, playlistImageUrls } from '../lib/displayImages';
import { upcomingEvents } from '../lib/events';
import { api } from '../lib/api';
import { startDisplaySync } from '../lib/displaySync';
import { advancePlayer, currentItem, initialPlayerState, reconcilePlayer, upcomingItem, type PlayerState } from '../lib/playerMachine';
import { getScheduleHighlights, isScheduleEligible, resolveClassSchedule } from '../lib/schedule';
import type { ClassSession, DisplayItem, DisplayPayload, EventFeed, TemporarySchedule, TemplateContent } from '../shared';

const CACHE_KEY_PREFIX = 'elevation-display-cache:v1:';
const SCHEDULE_TICK_MS = 15_000;

function readCachedPayload(slug: string): DisplayPayload | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(`${CACHE_KEY_PREFIX}${slug}`) ?? 'null') as DisplayPayload | null;
    if (!parsed || !Array.isArray(parsed.items) || parsed.screen?.slug !== slug) return null;
    return { ...parsed, mode: parsed.mode ?? 'manual', classSessions: parsed.classSessions ?? [] };
  } catch {
    return null;
  }
}

function FallbackScreen() {
  return (
    <main className="display-shell display-fallback" data-testid="display-fallback">
      <div className="fallback-mark" aria-label="Elevation Jiu Jitsu — Train. Evolve. Elevate.">
        <span className="fallback-rule" /><h1>ELEVATION<br /><strong>JIU JITSU</strong></h1>
        <p>TRAIN. EVOLVE. ELEVATE.</p><span className="fallback-rule fallback-rule--short" />
      </div>
    </main>
  );
}

function BrandHeader({ eyebrow }: { eyebrow: string }) {
  return <header className="portrait-brand"><img src="/branding/elevation-va-black.png" alt="Elevation" /><i /><small>{eyebrow}</small></header>;
}

const CATEGORY_LABELS: Record<string, string> = {
  'Fuel Point': 'FUEL POINT', 'Events & Competitions': 'EVENTS & COMPETITIES',
  'Memberships & Beginners': 'TRAIN MET ONS', 'Daily Schedule': 'DAGROOSTER',
  'Branding & Sponsors': 'ELEVATION', 'General Announcements': 'COMMUNITY & NIEUWS'
};

function BrandFooter({ children }: { children?: ReactNode }) {
  return <footer className="portrait-footer"><span>{children || 'TRAIN. EVOLVE. ELEVATE.'}</span></footer>;
}

export function SlideFrame({ category, children, callToAction, className = '' }: { category: string; children: ReactNode; callToAction?: string; className?: string }) {
  return <article className={`portrait-template ${className}`}><BrandHeader eyebrow={CATEGORY_LABELS[category] || 'ELEVATION'} /><div className="slide-middle">{children}</div><BrandFooter>{callToAction}</BrandFooter></article>;
}

export function TemplateSlide({ content, category }: { content: TemplateContent; category: string }) {
  const hasQr = Boolean(content.qrImageUrl);
  const title = content.kind === 'product' ? content.productName : content.kind === 'event' ? content.eventTitle : content.headline;
  return <SlideFrame category={category} callToAction={content.callToAction} className={`${content.kind}-template${hasQr ? ' template-with-qr' : ''}${content.imageUrl ? ' template-with-image' : ''}`}>
    {content.imageUrl && <img className="template-image" src={displayImageUrl(content.imageUrl)} decoding="async" alt="" />}
    <div className="template-copy">
      {content.kind !== 'announcement' && content.headline && <p>{content.headline}</p>}
      <h1>{title}</h1>
      {content.kind === 'product' && <strong className="template-price">{content.price}</strong>}
      {content.kind === 'event' && <><strong>{content.eventDate}</strong><span>{content.location}</span></>}
      {content.supportingText && <em>{content.supportingText}</em>}
    </div>
    {hasQr && <img className="template-qr" src={content.qrImageUrl} alt="Scan de QR-code om deel te nemen" />}
  </SlideFrame>;
}

const DAY_NAMES = ['ZONDAG', 'MAANDAG', 'DINSDAG', 'WOENSDAG', 'DONDERDAG', 'VRIJDAG', 'ZATERDAG'];

function amsterdamNow(at: Date): { weekday: number; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Amsterdam', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  const weekdays: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return { weekday: weekdays[get('weekday')] ?? 0, minutes: Number(get('hour')) * 60 + Number(get('minute')) };
}

export function ScheduleSlide({ sessions, at, temporarySchedules = [] }: { sessions: ClassSession[]; at: Date; temporarySchedules?: TemporarySchedule[] }) {
  const clock = amsterdamNow(at);
  const resolved = resolveClassSchedule(sessions, temporarySchedules, at);
  const today = resolved.sessions.filter((session) => session.active && session.dayOfWeek === clock.weekday).sort((a, b) => a.startTime.localeCompare(b.startTime));
  const { currentIds, upcomingId } = getScheduleHighlights(today, clock.minutes);
  return <article className={`portrait-schedule${today.length > 8 ? ' portrait-schedule--compact' : ''}`}>
    <BrandHeader eyebrow={resolved.temporary ? 'TIJDELIJK ROOSTER' : 'DAGROOSTER'} />
    <h1>{DAY_NAMES[clock.weekday]}</h1>
    <div className="schedule-cards">
      {today.length ? today.map((session) => <div key={session.id} className={`schedule-card${currentIds.includes(session.id) ? ' schedule-card--current' : session.id === upcomingId ? ' schedule-card--upcoming' : ''}`}>
        <time>{session.startTime}-{session.endTime}</time><i /><div>{currentIds.includes(session.id) && <span className="schedule-status">NU BEZIG</span>}{session.id === upcomingId && <span className="schedule-status">NEXT UP</span>}<strong>{session.title}</strong>{session.detail && <small>{session.detail}</small>}</div>{session.location && <b>{session.location}</b>}
      </div>) : <div className="schedule-empty"><strong>GEEN LESSEN VANDAAG</strong><span>Check de app voor het actuele programma.</span></div>}
    </div>
    <BrandFooter />
  </article>;
}

export function EventsSlide({ feed, at = new Date(), page = 0 }: { feed?: EventFeed; at?: Date; page?: number }) {
  const events = upcomingEvents(feed?.events ?? [], at);
  const start = (page % Math.max(1, Math.ceil(events.length / 6))) * 6;
  return <SlideFrame category="Events & Competitions" callToAction="events.elevationjiujitsu.nl" className="events-slide">
    <h1>AANKOMENDE EVENTS</h1><div className="schedule-cards event-cards">
      {events.length ? events.slice(start, start + 6).map((event, index) => <div key={event.id} className={`schedule-card event-card${start === 0 && index === 0 ? ' event-card--next' : ''}`}>
        <time>{event.dateLabel}</time><i /><div>{start === 0 && index === 0 && <span className="schedule-status">EERSTVOLGENDE EVENT</span>}<strong>{event.title}</strong><small>{event.location}</small></div>
      </div>) : <div className="schedule-empty"><strong>BINNENKORT MEER EVENTS</strong><span>Bekijk de eventspagina voor updates.</span></div>}
    </div>
    <div className="events-scan"><img src="/branding/events-qr.svg" alt="QR-code naar events.elevationjiujitsu.nl" /><div><strong>SCAN &amp; BEKIJK</strong><span>Alle events en teamplanners</span></div></div>
  </SlideFrame>;
}

interface MediaSlideProps {
  item: DisplayItem;
  classSessions: ClassSession[];
  temporarySchedules: TemporarySchedule[];
  eventFeed?: EventFeed;
  eventPage: number;
  onAdvance: () => void;
  onFailure: () => void;
}

function MediaSlide({ item, classSessions, temporarySchedules, eventFeed, eventPage, onAdvance, onFailure }: MediaSlideProps) {
  const watchdogRef = useRef<number | null>(null);
  const advanceRef = useRef(onAdvance);
  const failureRef = useRef(onFailure);
  useEffect(() => {
    advanceRef.current = onAdvance;
    failureRef.current = onFailure;
  }, [onAdvance, onFailure]);
  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current !== null) window.clearTimeout(watchdogRef.current);
    watchdogRef.current = null;
  }, []);

  useEffect(() => {
    if (item.media.type !== 'video') {
      const timer = window.setTimeout(() => advanceRef.current(), item.durationSeconds * 1000);
      return () => window.clearTimeout(timer);
    }
    watchdogRef.current = window.setTimeout(() => failureRef.current(), 20_000);
    return clearWatchdog;
  }, [clearWatchdog, item.id, item.media.type, item.durationSeconds]);

  if (item.media.type === 'events') return <EventsSlide feed={eventFeed} page={eventPage} />;
  if (item.media.type === 'schedule') return <ScheduleSlide sessions={classSessions} temporarySchedules={temporarySchedules} at={new Date()} />;
  if (item.media.type === 'template' && item.media.template) return <TemplateSlide content={item.media.template} category={item.media.category} />;
  if (item.media.type === 'image') {
    return <SlideFrame category={item.media.category}><img className={`display-media display-media--${item.fit}`} src={displayImageUrl(item.media.url)} decoding="async" alt="" draggable={false} onLoad={clearWatchdog} onError={onFailure} data-media-id={item.media.id} data-media-type="image" /></SlideFrame>;
  }
  return (
    <SlideFrame category={item.media.category}><video className={`display-media display-media--${item.fit}`} src={item.media.url} muted autoPlay playsInline preload="auto" disablePictureInPicture
      onLoadedData={clearWatchdog} onCanPlay={(event) => { clearWatchdog(); void event.currentTarget.play().catch(() => { watchdogRef.current = window.setTimeout(onFailure, 4_000); }); }}
      onEnded={onAdvance} onError={onFailure} data-media-id={item.media.id} data-media-type="video" /></SlideFrame>
  );
}

export function DisplayPage({ slug }: { slug: string }) {
  const [payload, setPayload] = useState<DisplayPayload | null>(() => readCachedPayload(slug));
  const [player, setPlayer] = useState<PlayerState>(initialPlayerState);
  const [clock, setClock] = useState(() => Date.now());
  const [landscapeViewport, setLandscapeViewport] = useState(() => window.innerWidth > window.innerHeight);
  const rotationPreference = new URLSearchParams(window.location.search).get('rotate') ?? 'auto';
  const rotation = rotationPreference === 'cw' || rotationPreference === 'ccw' ? rotationPreference : rotationPreference === 'none' ? 'none' : landscapeViewport ? 'cw' : 'none';

  useEffect(() => {
    const sync = startDisplaySync((signal) => api.display(slug, signal), (nextPayload) => {
      setPayload((previous) => previous?.screen.slug === slug && previous.revision > nextPayload.revision ? previous : nextPayload);
      try { localStorage.setItem(`${CACHE_KEY_PREFIX}${slug}`, JSON.stringify(nextPayload)); } catch { /* Playback survives a full cache. */ }
    });
    const resume = () => { if (document.visibilityState !== 'hidden') { setClock(Date.now()); void sync.refresh(); } };
    window.addEventListener('online', resume);
    window.addEventListener('focus', resume);
    window.addEventListener('pageshow', resume);
    document.addEventListener('visibilitychange', resume);
    const scheduleTick = window.setInterval(() => setClock(Date.now()), SCHEDULE_TICK_MS);
    return () => {
      sync.stop();
      window.clearInterval(scheduleTick);
      window.removeEventListener('online', resume);
      window.removeEventListener('focus', resume);
      window.removeEventListener('pageshow', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [slug]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    let registration: ServiceWorkerRegistration | undefined;
    let disposed = false;
    const hadController = Boolean(navigator.serviceWorker.controller);
    const reload = () => { if (hadController) window.location.reload(); };
    navigator.serviceWorker.addEventListener('controllerchange', reload);
    void navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).then((value) => { if (!disposed) registration = value; }).catch(() => undefined);
    const update = window.setInterval(() => { void registration?.update().catch(() => undefined); }, 60_000);
    return () => { disposed = true; window.clearInterval(update); navigator.serviceWorker.removeEventListener('controllerchange', reload); };
  }, []);
  useEffect(() => { const update = () => setLandscapeViewport(window.innerWidth > window.innerHeight); window.addEventListener('resize', update); return () => window.removeEventListener('resize', update); }, []);

  const eligibleItems = useMemo(() => (payload?.items ?? []).filter((item) => isScheduleEligible(item.schedule, new Date(clock))), [clock, payload]);
  const effectivePlayer = reconcilePlayer(player, eligibleItems);
  const activeItem = currentItem(effectivePlayer, eligibleItems);
  const nextItem = upcomingItem(effectivePlayer, eligibleItems);
  const nextVideoUrl = nextItem?.media.type === 'video' ? nextItem.media.url : '';
  const preloadKey = JSON.stringify(playlistImageUrls(eligibleItems));
  const advance = useCallback(() => setPlayer((state) => advancePlayer(reconcilePlayer(state, eligibleItems), eligibleItems)), [eligibleItems]);
  const fail = useCallback(() => setPlayer((state) => advancePlayer(reconcilePlayer(state, eligibleItems), eligibleItems, true)), [eligibleItems]);

  useEffect(() => {
    let cancelled = false;
    const urls = JSON.parse(preloadKey) as string[];
    void (async () => {
      // Sequential downloads avoid competing with the currently visible photo.
      for (const url of urls) {
        if (cancelled) break;
        const image = new Image();
        image.decoding = 'async';
        image.src = url;
        try { await image.decode(); } catch { /* A failed photo must not block subsequent preloads. */ }
      }
    })();
    return () => { cancelled = true; };
  }, [preloadKey]);

  useEffect(() => {
    if (!nextVideoUrl) return;
    const video = document.createElement('video');
    video.preload = 'auto'; video.muted = true; video.src = nextVideoUrl; video.load();
    return () => { video.removeAttribute('src'); video.load(); };
  }, [nextVideoUrl]);

  const rotationClass = rotation === 'none' ? '' : `display-rotation display-rotation--${rotation}`;
  if (activeItem?.media.id === 'a9c9ce7d-9180-4a96-847d-127ad9b32080' && eligibleItems.length === 1) return <div className={rotationClass}><DroneLogo /></div>;
  if (!activeItem) return <div className={rotationClass}><FallbackScreen /></div>;
  return <div className={rotationClass}><main className="display-shell" data-testid="display-player" data-revision={payload?.revision ?? 0}>
    <MediaSlide key={`${activeItem.id}:${effectivePlayer.cycle}`} item={activeItem} classSessions={payload?.classSessions ?? []} temporarySchedules={payload?.temporarySchedules ?? []} eventFeed={payload?.eventFeed} eventPage={effectivePlayer.cycle} onAdvance={advance} onFailure={fail} />
  </main></div>;
}
